/**
 * HTTP CLIENT STANDARD
 * ====================
 * This service uses apiClient from api.service.ts as the standardized HTTP client.
 *
 * WHY apiClient:
 * - Automatic CSRF token injection via request interceptors
 * - Automatic retry on 403 CSRF errors
 * - Centralized error handling and logging
 * - Type-safe responses
 * - Session cookie management (withCredentials: true)
 *
 * DO NOT use:
 * - Raw axios (missing interceptors)
 * - Native fetch (manual CSRF, no retry logic)
 * - Custom HTTP implementations
 *
 * See: /HTTP_CLIENT_STANDARDS.md
 *
 * MIGRATED: 2025-10-09 - Converted from raw axios to apiClient
 * REASON: Standardize on apiClient with automatic CSRF and error handling
 */

import type { OCRAnalysisRequest, OCRAnalysisResponse } from '../types/vehicle.types';
import { apiClient } from './api.service';
import { handleApiError } from '../utils/api-error-handler';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/**
 * Custom error class for OCR service errors
 */
export class OCRServiceError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: unknown,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'OCRServiceError';
    Object.setPrototypeOf(this, OCRServiceError.prototype);
  }
}

/**
 * Validate image file before upload
 */
export const validateImage = (file: File): { valid: boolean; error?: string } => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${ALLOWED_TYPES.join(', ')}`,
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${MAX_FILE_SIZE / 1024 / 1024}MB`,
    };
  }

  return { valid: true };
};

/**
 * Convert image file to base64
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const base64 = reader.result.split(',')[1];
        if (base64) {
          resolve(base64);
        } else {
          reject(new Error('Failed to extract base64 data'));
        }
      } else {
        reject(new Error('Failed to read file as base64'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Compress image for OCR (optimized for speed and smaller payload)
 * Uses WebP format for 30-40% smaller file sizes than JPEG
 */
export const compressImage = async (
  file: File,
  maxWidth = 640,
  maxHeight = 480,
  quality = 0.65
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/webp',
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));

      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Analyze image using OCR API
 */
export const analyzeImage = async (request: OCRAnalysisRequest): Promise<OCRAnalysisResponse> => {
  try {
    const response = await apiClient.post<OCRAnalysisResponse>('/api/v1/ocr/analyze', request);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'analyze image with OCR', 'OCRService');
  }
};

/**
 * Process image file directly (backward compatible name)
 * Validates, compresses, and analyzes image using OCR API
 */
export const processImageFile = async (
  file: File,
  fieldType: 'vin' | 'licensePlate' = 'vin'
): Promise<OCRAnalysisResponse> => {
  try {
    // Validate file
    const validation = validateImage(file);
    if (!validation.valid) {
      throw new Error(validation.error || 'Invalid image file');
    }

    // Compress and convert to base64
    const compressed = await compressImage(file);
    const base64 = await fileToBase64(new File([compressed], file.name, { type: 'image/webp' }));

    // Analyze with correct property names matching OCRAnalysisRequest
    return analyzeImage({
      image: base64,
      fieldType: fieldType,
    });
  } catch (error) {
    return handleApiError(error, 'process image file', 'OCRService');
  }
};

/**
 * Alias for backward compatibility
 * @deprecated Use processImageFile instead
 */
export const analyzeImageFile = processImageFile;

/**
 * Format OCR result for display
 * Cleans and formats extracted text based on field type
 */
export const formatOCRResult = (
  text: string,
  fieldType: 'vin' | 'licensePlate'
): string => {
  if (!text) return '';

  const cleaned = text.trim().toUpperCase();

  if (fieldType === 'vin') {
    // VIN: Remove non-alphanumeric, max 17 chars
    return cleaned.replace(/[^A-HJ-NPR-Z0-9]/gi, '').slice(0, 17);
  } else {
    // License Plate: Keep alphanumeric and spaces/dashes
    return cleaned.replace(/[^A-Z0-9\s-]/gi, '');
  }
};
